<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="InquiryList" label="Inquiries" />
      <breadcrumb-link
        v-if="inquiry"
        view="InquiryDetail"
        :label="pageTitle"
        :params="{ id: inquiry.id }"
        :active="true"
      />
    </breadcrumb-list>

    <div v-if="inquiry" :key="inquiry.id">
      <inquiry-expired
        v-if="isInquiryExpired"
        :inquiry-id="inquiry.id"
        :created-at="inquiry.created_at"
      ></inquiry-expired>

      <inquiry-detail-plan
        v-else
        :raw-plan="inquiry?.coverage?.plan"
        :createdAt="inquiry.created_at"
        :asOfDate="inquiry.as_of_date"
        :inquiry-id="inquiry.id"
        :payer="inquiry.payer"
        :response-payer="inquiry.coverage.payer_name"
        :patient="inquiry.coverage.patient"
        :subscriber="inquiry.coverage.subscriber"
        :provider="inquiry?.provider.name"
        :payer-name-override="inquiry.payer_name_override"
        :input-does-not-match-messages="inputDoesNotMatchMessages"
        :remote-error-messages="remoteErrorMessages"
        :patient-history="relatedInquiries"
        :patient-history-loading="loadingRelatedInquiries"
        @select-inquiry="onSelectInquiry"
        @reload-inquiries="reloadInquiries"
      ></inquiry-detail-plan>
    </div>
  </div>
</template>

<script>
import InquiryDetailPlan from '../components/InquiryDetailPlan.vue';
import InquiryExpired from '../components/InquiryExpired.vue';
import request from '../request';

export default {
  name: 'InquiryDetail',
  components: {
    InquiryDetailPlan,
    InquiryExpired,
  },
  computed: {
    pageTitle() {
      if (this.inquiry?.coverage?.patient) {
        let firstName = this.inquiry.coverage.patient.first_name;
        let lastName = this.inquiry.coverage.patient.last_name;
        return `${firstName} ${lastName}`;
      }
      return this.inquiry.id;
    },
    isInquiryExpired() {
      if (!this.inquiry) return false;
      const twoMonthsAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
      return !this.inquiry.coverage || this.inquiry.created_at < twoMonthsAgo;
    },
  },
  mounted() {
    this.processValidationMessages();
  },
  data() {
    return {
      inquiry: null,
      relatedInquiries: [],
      loadingRelatedInquiries: false,
      inputDoesNotMatchMessages: [],
      remoteErrorMessages: [],
    };
  },
  methods: {
    processValidationMessages() {
      // Check for remote errors and display them as separate warnings
      if (this.inquiry && this.inquiry.remote_errors) {
        const remoteErrors = this.inquiry.remote_errors;

        // Iterate through all keys in remote_errors object
        Object.keys(remoteErrors).forEach((key) => {
          const value = remoteErrors[key];
          if (value && typeof value === 'string' && value.trim() !== '') {
            this.remoteErrorMessages.push(`${key}: ${value}`);
          }
        });
      }

      if (this.inquiry && this.inquiry.coverage && this.inquiry.submission) {
        // check memberId
        if (
          this.inquiry.submission.memberId &&
          this.inquiry.submission.memberId !== '' &&
          this.inquiry.submission.memberId.toUpperCase().trim() !==
            this.inquiry.coverage.subscriber.member_id.toUpperCase().trim()
        ) {
          const inputMemberId = this.inquiry.submission.memberId;
          const remoteMemberId = this.inquiry.coverage.subscriber.member_id;
          const msg = `MemberID '${inputMemberId}' was entered, but the payer returned '${remoteMemberId}'`;
          this.inputDoesNotMatchMessages.push(msg);
        }

        // check birthDate
        const patientBirthDate = this.inquiry.submission.patientBirthDate ?? '';
        if (patientBirthDate !== '') {
          const inputBirthDate = patientBirthDate.split('T')[0];
          const remoteBirthDate = this.inquiry.coverage.patient.dob.split('T')[0];

          if (inputBirthDate !== remoteBirthDate) {
            const msg = `BirthDate '${inputBirthDate}' was entered, but the payer returned '${remoteBirthDate}'`;
            this.inputDoesNotMatchMessages.push(msg);
          }
        }

        // check first and last name
        if (
          this.inquiry.submission.patientFirstName !== '' &&
          this.inquiry.submission.patientLastName !== ''
        ) {
          let inputName = `${this.inquiry?.submission?.patientFirstName?.trim() ?? ''} ${
            this.inquiry?.submission?.patientLastName?.trim() ?? ''
          }`;
          inputName = inputName.toUpperCase();

          let remoteName = `${this.inquiry?.coverage?.patient?.first_name?.trim() ?? ''} ${
            this.inquiry?.coverage?.patient?.last_name?.trim() ?? ''
          }`;
          remoteName = remoteName.toUpperCase();

          if (inputName.trim() != '' && inputName !== remoteName) {
            const msg = `Name '${inputName}' was entered, but the payer returned '${remoteName}'`;
            this.inputDoesNotMatchMessages.push(msg);
          }
        }
      }
    },
    onSelectInquiry(id) {
      this.$router.push({ name: 'InquiryDetail', params: { id } });
    },
    async fetchInquiry(id, fetchAll = false) {
      try {
        if (fetchAll) {
          const resp = await request.get(`/inquiries/${id}`);
          const inquiry = resp.data.inquiry;
          if (inquiry.status !== 'complete' && inquiry.status !== 'expired') {
            this.$router.replace({ name: 'NotFound' });
            return;
          }
          this.inquiry = inquiry;
        }

        // Check if we have required fields for fetching history
        if (!this.inquiry.member_id || !this.inquiry.payer?.id) {
          this.loadingRelatedInquiries = false;
          return;
        }

        // Set loading state
        this.loadingRelatedInquiries = true;

        // Fetch related inquiries (patient history) - ungrouped
        const payerGroupId = this.inquiry.payer.payer_group_id || this.inquiry.payer.id;
        const related = await request.get('/inquiries', {
          params: {
            member_id: this.inquiry.member_id,
            payer_group_id: payerGroupId,
            is_grouped: false,
          },
        });
        this.relatedInquiries = related.data.inquiries;
      } catch {
        this.$buefy.toast.open({
          message: 'An error occurred while fetching inquiry.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingRelatedInquiries = false;
      }
    },
    reloadInquiries() {
      this.fetchInquiry(this.inquiry.id, true);
    },
  },
  beforeRouteEnter(to, from, next) {
    request
      .get(`/inquiries/${to.params.id}`)
      .then((resp) => {
        let status = resp.data.inquiry.status;
        if (status === 'complete' || status === 'expired') {
          next((vm) => {
            vm.inquiry = resp.data.inquiry;
            // Fetch related inquiries after inquiry is set
            vm.fetchInquiry();
          });
        } else {
          next({ name: 'NotFound' });
        }
      })
      .catch(() => {
        next({ name: 'NotFound' });
      });
  },
  beforeRouteUpdate(to, from, next) {
    // This is called when navigating to the same route with different params
    // (e.g., switching between inquiries in patient history)
    request
      .get(`/inquiries/${to.params.id}`)
      .then((resp) => {
        let status = resp.data.inquiry.status;
        if (status === 'complete' || status === 'expired') {
          // Reset data
          this.inquiry = null;
          this.relatedInquiries = [];
          this.inputDoesNotMatchMessages = [];
          this.remoteErrorMessages = [];

          // Set new inquiry
          this.inquiry = resp.data.inquiry;

          // Re-run all the validation logic from mounted()
          this.$nextTick(() => {
            this.processValidationMessages();
            this.fetchInquiry();
          });

          next();
        } else {
          next({ name: 'NotFound' });
        }
      })
      .catch(() => {
        next({ name: 'NotFound' });
      });
  },
};
</script>
